<template>
  <DatePicker
    ref="datepicker"
    v-bind="$props"
    :locale="localeFixed"
    :value="date"
    color="gray"
    @input="handleInput"
  />
</template>

<script>
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import { getDateByUnixSeconds, getUnixSeconds } from './convert';

export default {
  data() {
    return {
      date: null,
      isDateUnix: false,
      isDateRangeUnix: false,
    };
  },
  props: {
    columns: { type: Number, default: 1 },
    isRange: { type: Boolean, default: false },
    step: { type: Number, default: 1 },
    value: [Number, Object],
    locale: {
      type: String,
      default: 'pt-BR',
      validator: (value) => ['en-US', 'es-ES', 'pt-BR', 'en_US', 'es_ES', 'pt_BR'].includes(value),
    },
  },
  components: {
    DatePicker,
  },
  watch: {
    value(newValue) {
      this.setDateValue(newValue);

      const { datepicker } = this.$refs;
      if (newValue && this.isDateRangeUnix) {
        const focusDate = getDateByUnixSeconds(newValue.start);
        datepicker.focusDate(focusDate);
      }
    },
  },
  computed: {
    localeFixed() {
      return this.locale.replace('_', '-');
    },
  },
  methods: {
    handleInput(newValue) {
      const value = this.getDateValue(newValue);
      this.$emit('input', value);
    },
    getDateValue(date) {
      let value = date;

      if (date && this.isDateUnix) {
        value = getUnixSeconds(date);
      } else if (date && this.isDateRangeUnix) {
        const { start, end } = date;
        value = {
          start: getUnixSeconds(start),
          end: getUnixSeconds(end, true),
        };
      }

      return value;
    },
    setDateValue(dateUnix) {
      if (this.isDateUnix) {
        this.date = dateUnix ? getDateByUnixSeconds(dateUnix) : dateUnix;
      } else if (this.isDateRangeUnix) {
        const { start, end } = dateUnix;
        this.date = {
          start: start ? getDateByUnixSeconds(start) : start,
          end: end ? getDateByUnixSeconds(end) : end,
        };
      }
    },
    setDateType() {
      if (this.isRange) {
        this.isDateRangeUnix = true;
      } else {
        this.isDateUnix = true;
      }
    },
  },
  created() {
    this.setDateType();
    this.setDateValue(this.value);
  },
};
</script>

<style lang="scss">
.vc-container {
  --font-bold: 600;
  --text-sm: var(--track-font-size-xs) !important;
  --day-size: 32px;

  background-color: transparent;
  border: 0;
  color: var(--track-color-bluegray-60);
  font-family: var(--track-font-family-base), sans-serif;
}

.vc-pane-layout {
  gap: var(--track-spacing-lg);
}

.vc-pane {
  background-color: var(--track-color-white);
  border: 1px solid var(--track-color-bluegray-30);
  border-radius: var(--track-border-radius-sm);
  min-width: initial !important;
}

.vc-header {
  padding-bottom: var(--track-spacing-md) !important;
}

.vc-arrow {
  --gray-600: var(--track-color-bluegray-70);
}

.vc-title {
  --gray-800: var(--track-color-bluegray-70);
  --text-lg: var(--track-font-size-xs);
  text-transform: capitalize;
}

.vc-weeks {
  min-width: initial !important;
  padding: 0 var(--track-spacing-sm) var(--track-spacing-sm) !important;
}

.vc-weekday {
  --text-sm: var(--track-font-size-xxs) !important;
  --gray-500: var(--track-color-bluegray-70);
  --font-bold: 600;
}

.vc-day {
  height: var(--day-size) !important;
  width: var(--day-size) !important;
}

.vc-day-content {
  --font-bold: var(--track-font-weight-regular);
  --gray-900: var(--track-color-bluegray-90);
  height: var(--day-size) !important;
  line-height: var(--day-size) !important;
  width: var(--day-size) !important;

  .vc-highlights + &:hover,
  .vc-highlights + &:focus {
    --gray-900: var(--track-color-white);
    background-color: transparent !important;
  }
}

.vc-highlight {
  --gray-200: var(--track-color-bluegray-20);
  --gray-600: var(--track-color-bluegray-80);
  height: var(--day-size) !important;
  width: var(--day-size) !important;

  // NOTE: Estilizar o :hover e select do DatePicker (v-calendar)
  --white: var(--track-color-bluegray-80);
  --gray-700: var(--track-color-bluegray-80);
}

.on-left {
  margin-right: 0 !important;
}
.on-right {
  margin-left: 0 !important;
}
</style>
